import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";

const youtubeId = (url) =>
    url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/)?.[1];
const vimeoId = (url) => url?.match(/vimeo\.com\/(?:video\/)?(\d+)/)?.[1];
const isVideoFile = (url) => /\.(mp4|webm|mov)(\?.*)?$/i.test(url || "");

const Lightbox = ({ project, onClose, portrait = false }) => {
    useEffect(() => {
        const lenis = window.__lenis;
        lenis?.stop();
        document.body.style.overflow = "hidden";
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => {
            lenis?.start();
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    if (!project) return null;

    const yt = youtubeId(project.video);
    const vm = vimeoId(project.video);
    const file = !yt && !vm && isVideoFile(project.video);

    const mediaClass = portrait
        ? "h-[78vh] w-auto aspect-[9/16] max-w-full mx-auto"
        : "aspect-video w-full";

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="motion-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-xl sm:p-8"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`relative w-full overflow-hidden rounded-3xl border border-white/10 bg-ink-2 shadow-[0_40px_120px_-30px_rgba(0,0,0,1)] ${
                    portrait ? "max-w-full" : "max-w-4xl"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    data-testid="lightbox-close"
                    onClick={onClose}
                    aria-label="Close video player"
                    className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-ink/70 text-slate-300 backdrop-blur-md transition-colors duration-300 hover:border-flame/50 hover:text-white"
                >
                    <X size={18} />
                </button>

                <div className={mediaClass}>
                    {yt && (
                        <iframe
                            data-testid="lightbox-youtube"
                            src={`https://www.youtube.com/embed/${yt}?autoplay=1&rel=0`}
                            title={project.title}
                            className="h-full w-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                    {vm && (
                        <iframe
                            data-testid="lightbox-vimeo"
                            src={`https://player.vimeo.com/video/${vm}?autoplay=1`}
                            title={project.title}
                            className="h-full w-full"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                        />
                    )}
                    {file && (
                        <video
                            data-testid="lightbox-video"
                            src={project.video}
                            poster={project.thumbnail || undefined}
                            title={project.title}
                            className="h-full w-full object-contain bg-black"
                            controls
                            autoPlay
                            muted
                            playsInline
                        />
                    )}
                    {!project.video && (
                        <div className="relative h-full w-full">
                            <img
                                src={project.thumbnail}
                                alt={project.title}
                                className="h-full w-full object-cover opacity-40"
                            />
                            <div className="absolute inset-0 grid place-items-center">
                                <p className="rounded-full border border-white/15 bg-ink/70 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 backdrop-blur-md">
                                    Video coming soon
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-flame">
                            {project.category}
                        </p>
                        <h3
                            data-testid="lightbox-title"
                            className="mt-2 font-display text-xl font-semibold text-white sm:text-2xl"
                        >
                            {project.title}
                        </h3>
                        {project.description && (
                            <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-400">
                                {project.description}
                            </p>
                        )}
                    </div>
                    {project.url && (
                        <a
                            data-testid="lightbox-view-project"
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-flame px-6 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-flame-hover"
                        >
                            View Project
                            <ArrowUpRight size={15} />
                        </a>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
};

export default Lightbox;
